import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { usePermisos, rutaPermitida } from '../hooks/usePermisos';

export default function ProtectedRoute({ children }) {
  const { user, negocio, loading } = useAuth();
  const { permisos, cargando: cargandoPermisos } = usePermisos();
  const location = useLocation();

  // Gate por rol: si la ruta exige un permiso que el usuario no tiene, lo
  // mandamos al dashboard con un aviso (nunca pantalla en blanco). El toast
  // va en un efecto: no se disparan side-effects durante el render.
  const bloqueada = !loading && !cargandoPermisos && !!user && !!negocio
    && !rutaPermitida(permisos, location.pathname);
  useEffect(() => {
    if (bloqueada) toast.error('Tu rol no tiene acceso a esta sección.', { id: 'sin-permiso-ruta' });
  }, [bloqueada, location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-mezo-ink">
        <div className="w-8 h-8 border-4 border-mezo-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  // Sesión sin negocio → completar onboarding
  if (!negocio) return <Navigate to="/onboarding" replace />;

  // Desde 0044 la demo se activa SOLA (self-serve) — ya no existe la pantalla
  // "estamos revisando tu solicitud" que bloqueaba la app entera. Si algún
  // negocio quedara en 'pendiente_aprobacion' (dato viejo, la migración los
  // rescata), lo tratamos como sin plan: puede elegir en /planes, nunca un
  // spinner infinito.
  const sinPlan = !negocio.planId
    || negocio.planStatus === 'sin_plan'
    || negocio.planStatus === 'pendiente_aprobacion'
    || negocio.planStatus === 'suspendido';
  if (sinPlan && location.pathname !== '/planes') {
    return <Navigate to="/planes" replace state={{ obligatorio: true }} />;
  }

  // Primera carga de overrides de permisos (una sola vez por sesión): un
  // spinner breve evita redirigir a un empleado que SÍ tiene acceso por
  // override antes de saber que lo tiene. El admin no pasa por aquí.
  if (cargandoPermisos) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-mezo-ink">
        <div className="w-8 h-8 border-4 border-mezo-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Sin plan ya se resolvió arriba (el redirect a /planes es obligatorio y
  // no depende del rol) — aquí solo gateamos el resto de secciones.
  if (bloqueada) return <Navigate to="/dashboard" replace />;

  return children;
}
